import React, { createContext, useContext, useState, useCallback } from 'react';

interface QueryInspectorState {
  enabled: boolean;
  toggle: () => void;
  collectionName: string | null;
  schemaFields: string[];
  uiFields: string[];
  currentField: string | null;
  setCurrentField: (f: string | null) => void;
  register: (payload: { collectionName: string; schemaFields: string[]; uiFields: string[] }) => void;
  unregister: () => void;
}

const QueryInspectorContext = createContext<QueryInspectorState>({
  enabled: false,
  toggle: () => {},
  collectionName: null,
  schemaFields: [],
  uiFields: [],
  currentField: null,
  setCurrentField: () => {},
  register: () => {},
  unregister: () => {},
});

export const QueryInspectorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [enabled, setEnabled] = useState<boolean>(() => {
    try { return localStorage.getItem('qi.enabled') === 'true'; } catch { return false; }
  });
  const [collectionName, setCollectionName] = useState<string | null>(null);
  const [schemaFields, setSchemaFields] = useState<string[]>([]);
  const [uiFields, setUiFields] = useState<string[]>([]);
  const [currentField, setCurrentField] = useState<string | null>(null);

  const toggle = useCallback(() => {
    setEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem('qi.enabled', String(next)); } catch {}
      return next;
    });
  }, []);

  const register = useCallback((payload: { collectionName: string; schemaFields: string[]; uiFields: string[] }) => {
    setCollectionName(payload.collectionName);
    setSchemaFields(payload.schemaFields);
    setUiFields(payload.uiFields);
  }, []);

  const unregister = useCallback(() => {
    setCollectionName(null);
    setSchemaFields([]);
    setUiFields([]);
    setCurrentField(null);
  }, []);

  return (
    <QueryInspectorContext.Provider value={{ enabled, toggle, collectionName, schemaFields, uiFields, currentField, setCurrentField, register, unregister }}>
      {children}
    </QueryInspectorContext.Provider>
  );
};

export const useQueryInspector = () => useContext(QueryInspectorContext);
